import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { IndustryCard } from "@/components/IndustryCard";
import { INDUSTRIES } from "@/lib/site";
import {
  ShieldCheck,
  Home as HomeIcon,
  Briefcase,
  Hotel,
  GraduationCap,
  Landmark,
  Factory,
  Warehouse,
  ArrowRight,
  AlertTriangle,
  Layers,
  Wrench,
} from "lucide-react";

export const Route = createFileRoute("/industries")({
  component: () => <Outlet />,
  // Layout route — meta/canonical set on leaves to avoid duplicate <link rel="canonical">.
});

const INDUSTRY_ICONS: Record<string, typeof ShieldCheck> = {
  "luxury-residences": HomeIcon,
  "farmhouses-private-estates": HomeIcon,
  "developers-real-estate": Briefcase,
  "corporate-offices": Briefcase,
  "hospitality": Hotel,
  "education-auditoriums": GraduationCap,
  "government-infrastructure": Landmark,
  "industrial-facilities": Factory,
  "warehouses-logistics": Warehouse,
};

// Distilled per-industry briefing — challenges, relevant systems, common
// mistake, suggested stack, CTA. Mirrors and condenses the detail-page DETAILS
// so the hub itself becomes a useful SEO + conversion surface.
type IndustryBrief = {
  challenges: string;
  systems: string;
  mistake: string;
  stack: string;
  cta: { label: string; to?: "/industries/$slug" | "/contact"; slug?: string };
};

const BRIEFS: Record<string, IndustryBrief> = {
  "luxury-residences": {
    challenges:
      "Late automation planning, weak Wi-Fi, poor cinema room geometry, visible cameras, gate / visitor confusion and complex multi-app controls.",
    systems:
      "Smart automation, lighting control, curtain automation, CCTV, access control, fire alarm, gate automation, video door phone, home cinema, network / Wi-Fi and multi-room audio.",
    mistake:
      "Discussing technology after interiors and electrical layouts are frozen.",
    stack:
      "Smart automation + structured cabling + CCTV + access control + gate automation + video door phone + home cinema + Wi-Fi planning.",
    cta: { label: "Plan a Luxury Residence", to: "/industries/$slug", slug: "luxury-residences" },
  },
  "farmhouses-private-estates": {
    challenges:
      "Long, unlit perimeters, caretaker-dependent security with no audit trail, no remote visibility when the owner is away, weatherised outdoor systems and standby power for gates and cameras.",
    systems:
      "Perimeter protection, thermal + analytics, ANPR, gate automation, video door phone, CCTV, access control, owner-app automation, outdoor audio and private cinema.",
    mistake:
      "Treating the boundary wall as security on its own — with no detection layer or remote video verification.",
    stack:
      "Layered perimeter detection + ANPR + CCTV + owner-app automation + outdoor audio + private cinema + standby power planning.",
    cta: { label: "Plan a Private Estate", to: "/industries/$slug", slug: "farmhouses-private-estates" },
  },
  "developers-real-estate": {
    challenges:
      "Sales spec drifting from what is actually delivered, per-tower BOQs that differ, no operating model for handover, clubhouse AV specified after interiors freeze and under-specified ANPR / boom barriers.",
    systems:
      "Project-wide technology BOQ, sample-flat reference, CCTV, access control, boom barriers, ANPR, fire alarm, PA, clubhouse AV, common-area network and AMC structure.",
    mistake:
      "Each tower procured separately — three brands of CCTV in one project, with no documented handover to the RWA or facility manager.",
    stack:
      "Standardised technology BOQ + sample-flat reference + common-area security + clubhouse AV + handover & AMC pack.",
    cta: { label: "Review a Project Spec", to: "/industries/$slug", slug: "developers-real-estate" },
  },
  "corporate-offices": {
    challenges:
      "Inconsistent meeting-room UX across floors, visitor and access friction, security policies fragmented across floors and AV / network procured separately.",
    systems:
      "Standard room reference designs, touchless access, mobile credentials, visitor pre-registration, CCTV with analytics, converged IP backbone and audit-ready policy framework.",
    mistake:
      "Access control deployed door-by-door without a central policy engine — and a different AV experience in every room.",
    stack:
      "Reference rooms (huddle / mid / large / boardroom) + touchless access + integrated analytics + converged network + central policy.",
    cta: { label: "Standardise an Office Stack", to: "/industries/$slug", slug: "corporate-offices" },
  },
  hospitality: {
    challenges:
      "Guest experience tension with security, banquet AV complexity, network sprawl across BOH and guest-facing systems, and visible hardware conflicting with brand aesthetic.",
    systems:
      "Discreet surveillance, banquet & ballroom AV, spa and in-room audio, private screening, segmented property networks and central incident command.",
    mistake:
      "Banquet AV procured per-event instead of as a permanent system, with guest and BOH networks unsegmented.",
    stack:
      "Discreet CCTV + reconfigurable banquet AV + spa / cinema integrations + segmented IP networks + central command for incidents.",
    cta: { label: "Plan a Hospitality Property", to: "/industries/$slug", slug: "hospitality" },
  },
  "education-auditoriums": {
    challenges:
      "Speech intelligibility issues, hybrid teaching gaps, operator complexity, control rooms with no sightlines, and campus security treated as unrelated to auditorium AV.",
    systems:
      "Acoustic engineering, line-array / point-source loudspeakers, multi-camera lecture capture, LMS / streaming integration, stage lighting and rigging, and a one-touch operator UX.",
    mistake:
      "Loudspeakers and acoustics treated as a post-fit-out purchase — and hybrid capture added only after commissioning.",
    stack:
      "Acoustic shell + speaker design + lecture capture + stage & house control + campus security backbone.",
    cta: { label: "Plan an Auditorium", to: "/industries/$slug", slug: "education-auditoriums" },
  },
  "government-infrastructure": {
    challenges:
      "High-stakes perimeter security, compliance and data residency, multi-site command, cyber-vulnerable IP cameras, and command centres built without SOPs.",
    systems:
      "Sensor-fused perimeter (fibre + radar + thermal + analytics), hardened on-prem VMS, role-based access, ops-centre design with video-wall and SOPs, and policy / retention governance.",
    mistake:
      "Single-sensor perimeter without correlation or fusion — and vendor lock-in on closed VMS that blocks future analytics.",
    stack:
      "Sensor-fused perimeter + hardened VMS + ops centre + role-based access + compliance posture.",
    cta: { label: "Review a Critical Site", to: "/industries/$slug", slug: "government-infrastructure" },
  },
  "industrial-facilities": {
    challenges:
      "Perimeter risk, theft and intrusion, manual guarding dependency, poor CCTV coverage, weak access control, fire-safety coordination, and lack of central monitoring.",
    systems:
      "Perimeter protection, electric fence, thermal + analytics CCTV, AI video analytics, access control, fire alarm, boom barriers, ANPR, command centre and guard-response SOP.",
    mistake:
      "Treating CCTV as the only layer of security instead of building a layered, sensor-fused response system.",
    stack:
      "Perimeter detection + CCTV + access control + fire alarm + boom barriers + command centre + response SOP.",
    cta: { label: "Review Site Security", to: "/industries/$slug", slug: "industrial-facilities" },
  },
  "warehouses-logistics": {
    challenges:
      "Yard security gaps between gate, dock and yard, operational visibility limits, false alarms draining response, and paper-based visitor / driver management.",
    systems:
      "ANPR, dock-time monitoring, yard analytics (trailer / loitering / slot), perimeter detection, layered gates, multi-site command and operations-grade dashboards.",
    mistake:
      "Gate ANPR procured without dock-scheduling integration — and boom barriers / gate motors under-specified for actual peak-hour cycle count.",
    stack:
      "ANPR + dock monitoring + yard analytics + perimeter + gate hardware + multi-site command + dashboards.",
    cta: { label: "Plan a Logistics Hub", to: "/industries/$slug", slug: "warehouses-logistics" },
  },
};

export function IndustriesIndex() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Industries"
        title="Engineered for every operating context."
        description="From luxury residences to national infrastructure — each sector has a distinct risk model, user expectation, and technology canvas. Below: what we typically see, what we usually plan, and the mistakes we help projects avoid."
      />

      {/* Visual industry grid (preserved) */}
      <section className="py-20">
        <div className="container-page grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {INDUSTRIES.map((ind, i) => {
            const Icon = INDUSTRY_ICONS[ind.slug] ?? ShieldCheck;
            return (
              <Reveal key={ind.slug} delay={i * 0.04}>
                <IndustryCard
                  slug={ind.slug}
                  title={ind.title}
                  image={ind.icon}
                  index={i}
                  Icon={Icon}
                  titleAs="h2"
                />
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Deep per-industry briefings — SEO + conversion surface */}
      <section className="py-20 bg-surface border-y border-border">
        <div className="container-page">
          <div className="max-w-3xl">
            <span className="eyebrow">Sector Briefings</span>
            <h2 className="mt-4 text-balance text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
              The shape of each sector — at a glance.
            </h2>
            <p className="mt-5 text-ink-soft leading-relaxed max-w-2xl">
              Typical challenges, the systems usually in scope, the most expensive mistake we see,
              and the solution stack that keeps the project on track.
            </p>
          </div>

          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            {INDUSTRIES.map((ind, i) => {
              const brief = BRIEFS[ind.slug];
              if (!brief) return null;
              const Icon = INDUSTRY_ICONS[ind.slug] ?? ShieldCheck;
              return (
                <Reveal key={ind.slug} delay={i * 0.04}>
                  <article className="h-full rounded-2xl border border-border bg-background p-7 md:p-8">
                    <header className="flex items-start gap-4">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0">
                        <Icon className="h-5 w-5" strokeWidth={1.6} />
                      </span>
                      <div className="min-w-0">
                        <h3 className="text-xl font-semibold tracking-tight">
                          <Link
                            to="/industries/$slug"
                            params={{ slug: ind.slug }}
                            className="hover:text-primary transition-colors"
                          >
                            {ind.title}
                          </Link>
                        </h3>
                      </div>
                    </header>

                    <dl className="mt-6 space-y-5 text-sm">
                      <BriefRow Icon={AlertTriangle} term="Typical challenges" def={brief.challenges} />
                      <BriefRow Icon={Layers} term="Relevant systems" def={brief.systems} />
                      <BriefRow
                        Icon={AlertTriangle}
                        term="Common planning mistake"
                        def={brief.mistake}
                        accent
                      />
                      <BriefRow Icon={Wrench} term="Suggested solution stack" def={brief.stack} />
                    </dl>

                    <div className="mt-7 flex items-center justify-between gap-4 pt-5 border-t border-border">
                      {brief.cta.to === "/contact" ? (
                        <Link
                          to="/contact"
                          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-semibold uppercase tracking-widest text-primary-foreground hover:opacity-90 transition"
                        >
                          {brief.cta.label} <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      ) : (
                        <Link
                          to="/industries/$slug"
                          params={{ slug: brief.cta.slug ?? ind.slug }}
                          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-semibold uppercase tracking-widest text-primary-foreground hover:opacity-90 transition"
                        >
                          {brief.cta.label} <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      )}
                      <Link
                        to="/contact"
                        className="text-xs font-medium text-ink-soft hover:text-primary transition-colors uppercase tracking-widest"
                      >
                        Let's Talk →
                      </Link>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function BriefRow({
  Icon,
  term,
  def,
  accent,
}: {
  Icon: typeof ShieldCheck;
  term: string;
  def: string;
  accent?: boolean;
}) {
  return (
    <div>
      <dt
        className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest ${
          accent ? "text-primary" : "text-ink-soft"
        }`}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
        {term}
      </dt>
      <dd className="mt-1.5 text-foreground/90 leading-relaxed">{def}</dd>
    </div>
  );
}
